import { Card, CardContent, Box, Typography, useTheme } from '@mui/material';
import { ReactNode } from 'react';

interface MetricCardProps {
  title: string;
  value: string | number;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  icon?: ReactNode;
  subtitle?: string;
  subtitleTitle?: string;
}

const MetricCard = ({
  title,
  value,
  color = 'primary',
  icon,
  subtitle,
  subtitleTitle
}: MetricCardProps) => {
  const theme = useTheme();

  const colorMap = {
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    info: theme.palette.info.main,
  };

  const selectedColor = colorMap[color];

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: '10px',
        boxShadow: '0 10px 30px rgba(146,153,184,0.15)',
        border: 'none',
        overflow: 'visible',
        position: 'relative',
        '&:hover': {
          boxShadow: '0 15px 35px rgba(146,153,184,0.20)',
          transform: 'translateY(-2px)',
        },
        transition: 'all 0.3s ease',
      }}
    >
      <CardContent sx={{ padding: '20px 20px !important' }}>
        <Box sx={{
          display: 'flex',
          alignItems: subtitle ? 'flex-start' : 'center',
          gap: 2,
          minHeight: subtitle ? 'auto' : '80px',
        }}>
          {/* Icon */}
          {icon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '48px',
                height: '48px',
                borderRadius: '10px',
                backgroundColor: `${selectedColor}15`,
                color: selectedColor,
                flexShrink: 0,
                '& svg': {
                  fontSize: '24px',
                },
              }}
            >
              {icon}
            </Box>
          )}

          {/* Content */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
          }}>
            {/* Title */}
            <Typography
              variant="body2"
              sx={{
                fontSize: '12px',
                color: theme.palette.text.secondary,
                fontWeight: 500,
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {title}
            </Typography>

            {/* Metric Value */}
            <Typography
              variant="h1"
              sx={{
                fontSize: '32px',
                fontWeight: 700,
                color: selectedColor,
                lineHeight: 1.2,
              }}
            >
              {value}
            </Typography>

            {/* Subtitle */}
            {subtitle && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: '11px',
                  color: theme.palette.text.secondary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  marginTop: '4px',
                }}
                title={subtitleTitle || subtitle}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Colored Accent Bar */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '3px',
            backgroundColor: selectedColor,
            borderRadius: '0 0 10px 10px',
          }}
        />
      </CardContent>
    </Card>
  );
};

export default MetricCard;
